import React from "react";
import { useTranslation } from "react-i18next";
import { Linkedin, Instagram, Mail, Shield } from "lucide-react";

export default function Footer() {
  const { t } = useTranslation();

  const scrollToSection = (href) => {
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const solutionsLinks = [
    { label: "For Clinics", href: "/for-clinics", type: "route" },
    { label: "For Education", href: "/for-education", type: "route" },
    { label: "For Companies", href: "/for-companies", type: "route" },
    { label: "For Individuals", href: "/for-individuals", type: "route" },
    { label: "Pilot Program", href: "/pilot-program", type: "route" },
  ];

  const companyLinks = [
    { label: t('footer.aboutUs'), href: "#team", type: "scroll" },
    { label: t('nav.insights'), href: "/insights", type: "route" },
    { label: t('nav.contact'), href: "#contact", type: "scroll" },
  ];

  const trustLinks = [
    { label: "Trust & Safety", href: "/trust-safety", type: "route" },
    { label: "Privacy & Data Protection", href: "/privacy", type: "route" },
    { label: t('footer.gdprCompliance'), href: "/privacy", type: "route" },
  ];

  return (
    <footer
      data-testid="footer"
      className="bg-slate-900 pt-20 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Main footer content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 pb-12 border-b border-slate-800">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <a href="#" className="flex items-center gap-2 mb-6">
              <img
                src="/brand/psytech-logo-128.png"
                srcSet="/brand/psytech-logo-128.png 1x, /brand/psytech-logo-256.png 2x"
                alt="PsyTech"
                width="40"
                height="45"
                className="h-10 w-auto select-none"
                draggable={false}
              />
              <span className="text-xl font-bold text-white font-['Plus_Jakarta_Sans']">
                PsyTech
              </span>
            </a>
            <p className="text-slate-400 leading-relaxed mb-6 max-w-sm">
              {t('footer.tagline')}
            </p>

            {/* Company legal identity */}
            <div
              className="mb-6 text-xs text-slate-500 leading-relaxed"
              data-testid="footer-company-legal"
            >
              <p className="text-slate-300 font-semibold">PsyTech B.V.</p>
              <p className="mt-0.5">
                Registered in the Netherlands · KVK <span className="font-mono text-slate-400">42048210</span>
              </p>
            </div>

            {/* GDPR badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-full text-sm">
              <Shield className="w-4 h-4 text-green-400" />
              <span className="text-slate-300">{t('footer.gdpr')}</span>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="text-white font-semibold mb-4 font-['Plus_Jakarta_Sans']">Solutions</h4>
            <ul className="space-y-3">
              {solutionsLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-cyan-400 transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4 font-['Plus_Jakarta_Sans']">{t('footer.company')}</h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  {link.type === "scroll" ? (
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-slate-400 hover:text-cyan-400 transition-colors text-sm"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <a
                      href={link.href}
                      className="text-slate-400 hover:text-cyan-400 transition-colors text-sm"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Trust & Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4 font-['Plus_Jakarta_Sans']">{t('footer.legal')}</h4>
            <ul className="space-y-3">
              {trustLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-cyan-400 transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              <li>
                <a href="mailto:info@psy-tech.nl" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm">
                  info@psy-tech.nl
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 rounded-xl bg-slate-800/60 border border-slate-700 px-5 py-4">
          <p className="text-xs text-slate-400 leading-relaxed">
            <strong className="text-slate-200">Disclaimer:</strong> PsyTech provides digital assessment support and educational information.
            It does not provide diagnosis, treatment, medication advice, or emergency support. AI-generated insights should be reviewed by qualified professionals before clinical use.
          </p>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>

          {/* Social links */}
          <div className="flex items-center gap-4">
            <a
              href="https://www.linkedin.com/company/psytech-nl"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-all"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a
              href="https://instagram.com/psytech.nl"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-all"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="mailto:info@psy-tech.nl"
              aria-label="Email"
              className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-all"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* EU Data notice */}
        <div className="mt-8 text-center">
          <p className="text-xs text-slate-600">
            {t('footer.dataNotice')}
          </p>
        </div>
      </div>
    </footer>
  );
}
